import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { countryMarketInfoMapper, findCountryCodeFromUserContext, TCountryMarketInfo } from '../utils/countries.ts';

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TDynamicStoreField = Record<string, any>

export type TMarketingConsent = {
  casino: {
    contactBySms: boolean;
    contactByEmail: boolean;
    contactByPhone: boolean;
    contactByPost: boolean;
    contactByPushNotification?: boolean;
  };
  sports: {
    contactBySms: boolean;
    contactByEmail: boolean;
    contactByPhone: boolean;
    contactByPost: boolean;
    contactByPushNotification?: boolean;
  };
};

type RegistrationState = {
  // The current market identifier (typically a country code)
  // Used to determine locale-specific settings and form data
  currentMarket: string;
  setCurrentMarket: (market: string) => void;

  isAddressSearchOpen: boolean;
  setIsAddressSearchOpen: (isOpen: boolean) => void;

  // Stores all form input values from the registration process
  // Flexible structure using a dynamic Record type for various form fields
  formData: TDynamicStoreField | null;
  setFormData: (data: TDynamicStoreField) => void;

  // Loads saved form data from localStorage and sets defaults
  initializeFormData: (market: TCountryMarketInfo) => void;

  // Clears from data from store state
  clearFormData: (market: TCountryMarketInfo) => void;

  // Contains metadata about the registration process
  // Includes states like form open status, redirect flags, etc.
  metadata: TDynamicStoreField | null;
  setMetadata: (data: TDynamicStoreField) => void;

  // Loads saved metadata from localStorage with runtime state preservation
  initializeMetadata: (market: string) => void;

  /**
   *  Tracks validation errors by field name that display immediately
   *  Key is the field name, value is the error message
   *  Used for validations that should trigger and display right away (e.g., onBlur, onClick "Next" button)
   *  When the user clicks "Next", both customValidations and errors are checked/displayed
   */
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;

  /**
   *  Stores custom validation rules by field name
   *  Used for validations that should NOT trigger immediately (e.g., on component init, onChange events)
   *  These validation messages are stored but only displayed when the user clicks "Next"
   *  Example: Required field validations that shouldn't show errors until a form submission attempt
   */
  customValidations: Record<string, string>;
  setCustomValidations: (validations: Record<string, string>) => void;

  // Tracks conditional display logic for form elements
  // Key is the condition name, value is boolean state
  conditions: Record<string, boolean>;
  setCondition: (name: string, condition: boolean) => void;

  // Marketing consents selected by the user (per vertical)
  marketingConsent?: TMarketingConsent;
  setMarketingConsent: (consent: TMarketingConsent) => void;

  // Indicates if a section or component is in loading state
  // Used for partial UI loading indicators
  loading: boolean;
  setLoading: (isLoading: boolean) => void;

  // Indicates if the entire application is in loading state
  // Used for full-page loading indicators during initialization or pending requests
  rootLoading: boolean;
  setRootLoading: (isLoading: boolean) => void;

  // Tracks when the registration process started
  // Used for analytics or timing operations
  startTime: Date | null;
  setStartTime: (time: Date | null) => void;

  // True while a registration request is in flight, guards against double submits
  isRegistering: boolean;
  setIsRegistering: (isRegistering: boolean) => void;

  // Tracks errors during the registration process
  registerError: string | null;
  setRegisterError: (error: string | null) => void;

  // Validate on render
  validateOnRender: boolean;
  setValidateOnRender: (validate: boolean) => void;
};

export const useRegistrationStore = create<RegistrationState>()(
  devtools((set) => ({
    currentMarket:          '',
    setCurrentMarket:       (market) => set({ currentMarket: market }, false, 'setCurrentMarket'),
    isAddressSearchOpen:    false,
    setIsAddressSearchOpen: (isOpen: boolean) => set({ isAddressSearchOpen: isOpen }, false, 'setIsAddressSearchOpen'),
    formData:               null,
    setFormData:            (data) => set((state) => ({
      formData: {
        ...state.formData,
        ...data,
      },
    }), false, 'setFormData'),
    initializeFormData: (marketInfo) => {
      const { market, countryCode, phonePrefix, pinCountry } = marketInfo;

      const data = localStorage.getItem(`data_${market}`);
      set(() => {
        const oldFormData = data ? JSON.parse(data) : {};

        const userCountryCode = pinCountry ? countryCode : findCountryCodeFromUserContext();

        const defaultCountryCode = userCountryCode || countryCode;

        // Get phone prefix from country market mapper if available
        const defaultPhonePrefix = userCountryCode
          ? countryMarketInfoMapper
            .find(country =>
              country.countryCode === userCountryCode)?.phonePrefix || phonePrefix || '+353'
          : phonePrefix || '+353';

        const defaultFormData = {
          ...oldFormData,
          gender:             oldFormData.gender ?? 'male',
          countryOfResidence: oldFormData.countryOfResidence ?? defaultCountryCode,
          phonePrefix:        oldFormData.phonePrefix ?? defaultPhonePrefix,
          nationality:        oldFormData.nationality ?? defaultCountryCode,
          countryOfBirth:     oldFormData.countryOfBirth ?? defaultCountryCode,
        };

        return { formData: defaultFormData };
      }, false, 'initializeFormData');
    },
    clearFormData: (marketInfo) => {
      const { countryCode, phonePrefix, pinCountry } = marketInfo;

      const userCountryCode = pinCountry ? countryCode : findCountryCodeFromUserContext();
      const defaultCountryCode = userCountryCode || countryCode;

      // Get phone prefix from country market mapper if available
      const defaultPhonePrefix = userCountryCode
        ? countryMarketInfoMapper
          .find(country =>
            country.countryCode === userCountryCode)?.phonePrefix || phonePrefix || '+353'
        : phonePrefix || '+353';

      set(() => ({
        formData: {
          gender:             'male',
          countryOfResidence: defaultCountryCode,
          nationality:        defaultCountryCode,
          countryOfBirth:     defaultCountryCode,
          phonePrefix:        defaultPhonePrefix,
        },
      }), false, 'clearFormData')
    },

    metadata:    {},
    setMetadata: (data) => set((state) => {
      // Check if isOpen (modal open/closed) is being updated
      if ('isOpen' in data) {
        sessionStorage.setItem('fabric:registration:open', String(data.isOpen));
      }

      return {
        metadata: {
          ...state.metadata,
          ...data,
        },
      };
    }, false, 'setMetadata'),
    initializeMetadata: (market) => {
      const data = localStorage.getItem(`metadata_${market}`);
      set((state) => {
        const oldMetadata = data ? JSON.parse(data) : {};

        // Preserve existing runtime states that shouldn't be overwritten from storage
        const preservedStates = {
          isOpen:        state.metadata?.isOpen,
          hasRedirected: state.metadata?.hasRedirected,
          //identificationNumberTypeES: state.metadata?.identificationNumberTypeES,
        };

        return {
          metadata: {
            ...oldMetadata,
            ...preservedStates,
          },
        };
      }, false, 'initializeMetadata');
    },

    errors:    {},
    setErrors: (errors) => set((state) => ({
      errors: {
        ...state.errors,
        ...errors,
      },
    }), false, 'setErrors'),

    customValidations:    {},
    setCustomValidations: (customValidations) => set((state) => ({
      customValidations: {
        ...state.customValidations,
        ...customValidations,
      },
    }), false, 'setCustomValidations'),

    conditions:   {},
    setCondition: (name, condition) => set((state) => ({
      conditions: {
        ...state.conditions,
        [name]: condition,
      },
    }), false, 'setCondition'),

    marketingConsent:    undefined,
    setMarketingConsent: (consent) => set(() => ({
      marketingConsent: consent,
    }), false, 'setMarketingConsent'),

    loading:        false,
    setLoading:     (isLoading) => set({ loading: isLoading }),
    rootLoading:    false,
    setRootLoading: (isLoading) => set({ rootLoading: isLoading }),

    startTime:    null,
    setStartTime: (time) => set({ startTime: time }),

    isRegistering:    false,
    setIsRegistering: (isRegistering) => set({ isRegistering }, false, 'setIsRegistering'),

    registerError:    null,
    setRegisterError: (error) => set(() => ({
      registerError: error,
    }), false, 'setRegisterError'),

    validateOnRender:    false,
    setValidateOnRender: (validate: boolean) => set({ validateOnRender: validate }),
  }), { name: 'RegistrationStore', enabled: true }),
);

export const isRegistrationInFlight = () => useRegistrationStore.getState().isRegistering;
